<template>
  <v-dialog :model-value="modelValue" max-width="1100" persistent @update:model-value="$emit('update:modelValue', $event)">
    <v-card>
      <v-card-title style="font-size: 16px">{{ $t('graphRagProposals') }}</v-card-title>
      <v-card-text>
        <v-alert type="info" color="#C32E0E" variant="tonal" density="compact" class="mb-3">{{ reviewOnly ? $t('graphRagProposalsReviewHint') : $t('graphRagProposalsHint') }}</v-alert>

        <v-tabs v-model="kind" density="compact" class="mb-3">
          <v-tab value="node" data-testid="proposal-tab-node">{{ $t('graphRagNodes') }} ({{ counts.node }})</v-tab>
          <v-tab value="edge" data-testid="proposal-tab-edge">{{ $t('graphRagEdges') }} ({{ counts.edge }})</v-tab>
          <v-tab value="schema" data-testid="proposal-tab-schema">{{ $t('graphRagOntology') }} ({{ counts.schema }})</v-tab>
        </v-tabs>

        <v-data-table :items="rows" :headers="headers" :loading="loading" :row-props="rowProps" density="compact" data-testid="proposal-table">
          <template #no-data>
            <div style="text-align: center; color: #646464">{{ $t('noDataAvailable') }}</div>
          </template>

          <template #[`item.detail`]="{ item }">
            <div v-if="item.kind === 'node'" class="proposal-detail">
              <strong>{{ item.label }}</strong>
              <div style="font-size: 12px; color: #646464">{{ item.nodeKey }} · {{ item.nodeType }}</div>
            </div>
            <div v-else-if="item.kind === 'edge'" class="proposal-detail">
              {{ item.sourceNodeKey }} —[{{ item.relationType }}]→ {{ item.targetNodeKey }}
            </div>
            <div v-else>{{ item.typeKind }} · {{ item.typeId }}</div>
          </template>

          <template #[`item.status`]="{ item }">
            <v-chip size="x-small" :color="statusColour(item.status)">{{ item.status }}</v-chip>
            <!-- ชนิดที่ยังไม่มีในกราฟบล็อกการนำเข้า ต้องบอกให้ชัดว่าติดตรงไหน -->
            <v-chip v-if="item.isUnknownType" size="x-small" color="warning" class="ml-1">
              {{ $t('graphRagUnknownType') }}
            </v-chip>
          </template>

          <template #[`item.manage`]="{ item }">
            <template v-if="item.kind === 'schema'">
              <v-btn
                size="x-small"
                variant="text"
                :data-testid="`proposal-promote-${item.id}`"
                :disabled="!canEdit || item.status === 'applied'"
                @click="canEdit && item.status !== 'applied' && promote(item)"
              >
                {{ $t('graphRagPromoteType') }}
              </v-btn>
            </template>
            <template v-else>
              <v-btn
                size="x-small"
                variant="text"
                :data-testid="`proposal-accept-${item.id}`"
                :disabled="!canAct(item, 'accepted')"
                @click="canAct(item, 'accepted') && act(item, 'accept')"
              >
                {{ $t('graphRagAccept') }}
              </v-btn>
              <v-btn
                size="x-small"
                variant="text"
                color="error"
                :data-testid="`proposal-reject-${item.id}`"
                :disabled="!canAct(item, 'rejected')"
                @click="canAct(item, 'rejected') && act(item, 'reject')"
              >
                {{ $t('graphRagReject') }}
              </v-btn>
            </template>
          </template>
        </v-data-table>

        <!-- นำเข้าแล้วแต่มีรายการถูกข้าม — กล่องนี้ค้างไว้ให้อ่าน (ไม่มีคำเตือน = ตัวที่เรียกปิดหน้าต่างให้เอง) -->
        <v-alert v-if="applyResult" type="success" variant="tonal" density="compact" class="mt-3" aria-live="polite" data-testid="proposal-apply-result">
          <div style="font-size: 13px">
            {{ $t('graphRagProposalAppliedSummary', { nodes: applyResult.nodeCount, edges: applyResult.edgeCount }) }}
            <span v-if="applyResult.versionNo"> · {{ $t('graphRagVersion') }} v{{ applyResult.versionNo }}</span>
          </div>
          <ul v-if="applyResult.warnings && applyResult.warnings.length" class="mt-1" style="font-size: 12px; color: #646464">
            <li v-for="(w, i) in applyResult.warnings" :key="i">{{ w }}</li>
          </ul>
        </v-alert>
      </v-card-text>
      <!-- reviewOnly = ตรวจก่อนสร้างใน Import Wizard: ยังไม่เขียนลงกราฟที่นี่ กลับไปกด Create nodes ที่ขั้น 5 (เปลี่ยนโมเดลได้ก่อน) -->
      <v-card-actions v-if="reviewOnly">
        <v-spacer />
        <v-btn color="#C32E0E" variant="flat" data-testid="proposal-back-to-create" @click="$emit('update:modelValue', false)">
          {{ $t('graphRagProposalBackToCreate') }}
        </v-btn>
      </v-card-actions>
      <v-card-actions v-else>
        <v-spacer />
        <v-btn
          :variant="applyResult ? 'flat' : 'text'"
          :color="applyResult ? '#C32E0E' : undefined"
          data-testid="proposal-close"
          @click="$emit('update:modelValue', false)"
        >
          {{ applyResult ? $t('graphRagProposalDone') : $t('close') }}
        </v-btn>
        <v-btn
          color="#C32E0E"
          :variant="applyResult ? 'text' : 'elevated'"
          data-testid="proposal-apply"
          :loading="applying"
          :disabled="!canEdit"
          @click="canEdit && applyAll()"
        >
          {{ $t('graphRagApplyAll') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'ProposalReview',
  props: {
    modelValue: { type: Boolean, default: false },
    graphKey: { type: String, required: true },
    jobId: { type: String, default: null },
    canEdit: { type: Boolean, default: false },
    /** true = แค่ตรวจ (Accept/Reject) ไม่มีปุ่ม Apply — ผู้เรียกนำเข้าเองทีหลัง */
    reviewOnly: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'notify', 'applied'],
  data() {
    return {
      kind: 'node',
      rows: [],
      counts: { node: 0, edge: 0, schema: 0 },
      loading: false,
      applying: false,
      applyResult: null,
    };
  },
  computed: {
    headers() {
      return [
        { title: this.$t('detail') || 'detail', value: 'detail', sortable: false, width: '48%' },
        { title: this.$t('graphRagStatus'), value: 'status', sortable: false, width: '22%' },
        { title: this.$t('manage'), value: 'manage', align: 'center', sortable: false, width: '30%' },
      ];
    },
  },
  watch: {
    modelValue(open) {
      if (open && this.jobId) this.loadAll();
    },
    /** งานใหม่ = ผลนำเข้าของงานก่อนต้องไม่ค้างโชว์ */
    jobId() {
      this.applyResult = null;
    },
    kind() {
      this.load();
    },
  },
  methods: {
    notify(text, colour) {
      this.$emit('notify', { text, colour: colour || 'success' });
    },
    statusColour(status) {
      if (status === 'applied') return 'success';
      if (status === 'rejected') return 'grey';
      if (status === 'accepted') return '#C32E0E';

      return 'warning';
    },
    /** ปุ่มรับ/ปฏิเสธกดได้เมื่อแก้ได้ ยังไม่เข้ากราฟ และสถานะยังไม่ใช่ค่าที่ปุ่มนั้นจะตั้ง (กดซ้ำไม่มีผลอะไร) */
    canAct(item, target) {
      return this.canEdit && item.status !== 'applied' && item.status !== target;
    },
    /** แถวที่ปฏิเสธแล้วจางลง + ขีดฆ่า ให้เห็นทันทีว่ารายการนี้จะไม่เข้ากราฟ */
    rowProps({ item }) {
      return item && item.status === 'rejected' ? { class: 'proposal-row--rejected' } : {};
    },
    proposalName(item) {
      if (item.kind === 'edge') return `${item.sourceNodeKey} → ${item.targetNodeKey}`;

      return item.label || item.nodeKey || '';
    },
    loadAll() {
      ['node', 'edge', 'schema'].forEach((k) => {
        this.$call.GraphRagGetProposals(
          k,
          this.graphKey,
          this.jobId,
          (res) => {
            if (res.status !== 0) return;

            this.counts[k] = (res.data || []).length;
            if (k === this.kind) this.rows = res.data || [];
          },
          () => {}
        );
      });
    },
    load() {
      if (!this.jobId) return;

      this.loading = true;

      this.$call.GraphRagGetProposals(
        this.kind,
        this.graphKey,
        this.jobId,
        (res) => {
          this.loading = false;
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.rows = res.data || [];
        },
        () => {
          this.loading = false;
          this.notify(this.$t('graphRagRequestFailed'), 'error');
        }
      );
    },
    /**
     * เปลี่ยนสถานะในตารางทันที (ไม่รอโหลดใหม่) แล้วค่อยยืนยันกับเซิร์ฟเวอร์ — ล้มเมื่อไหร่คืนค่าเดิม
     * เดิมรอ load() ทั้งตาราง ผู้ใช้เห็นแค่ชิปเล็ก ๆ เปลี่ยนสี จึงเข้าใจว่ากดแล้วไม่เกิดอะไร
     */
    act(item, action) {
      const row = this.rows.find((r) => r.id === item.id) || item;
      const previous = row.status;
      row.status = action === 'accept' ? 'accepted' : 'rejected';

      const revert = (message) => {
        row.status = previous;
        this.notify(message || this.$t('graphRagRequestFailed'), 'error');
      };

      this.$call.GraphRagProposalAction(
        { graphKey: this.graphKey, proposalId: item.id, action: action },
        (res) => {
          if (res.status !== 0) return revert(res.message);

          return this.notify(this.$t(action === 'accept' ? 'graphRagProposalAccepted' : 'graphRagProposalRejected', { name: this.proposalName(row) }), action === 'accept' ? 'success' : 'info');
        },
        () => revert('')
      );
    },
    promote(item) {
      this.$call.GraphRagPromoteSchemaType(
        { graphKey: this.graphKey, proposalId: item.id },
        (res) => {
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.notify(this.$t('graphRagSaved'), 'success');
          this.loadAll();
        },
        () => this.notify(this.$t('graphRagRequestFailed'), 'error')
      );
    },
    applyAll() {
      this.applying = true;

      this.$call.GraphRagApplyProposals(
        { graphKey: this.graphKey, jobId: this.jobId },
        (res) => {
          this.applying = false;
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.applyResult = res.data;
          this.notify(this.$t('graphRagProposalAppliedSummary', { nodes: res.data.nodeCount, edges: res.data.edgeCount }), 'success');
          this.loadAll();
          // ส่งผลไปด้วย — ตัวเรียกใช้ตัดสินว่าจะปิดหน้าต่างเลยหรือค้างไว้ให้อ่านคำเตือน
          this.$emit('applied', res.data);
        },
        () => {
          this.applying = false;
          this.notify(this.$t('graphRagRequestFailed'), 'error');
        }
      );
    },
  },
};
</script>

<style scoped>
.proposal-row--rejected .proposal-detail {
  opacity: 0.5;
  text-decoration: line-through;
}
</style>
